import {Site} from "./site.model";
import {Company} from "./company.model";
import {DeviceType, LayoutID} from "./device-type.model";
import {DeviceInfo} from "./device-info.model";
import {DatapointModel} from "./datapoint.model";
import {AttributeID, DeviceAttribute} from "./device-attribute.model";
import moment from 'moment';

export const DeviceStatus = {
  storage: 'IN_STORAGE',
  installed: 'INSTALLED',
  unknown: 'UNKNOWN'
};

export const DeviceTypeID = {
  axing_datalogger: 4,
  countis_e44: 58
};

export const DataLoggerDefaults = {
  port: 12345,
  device: 200
}

export const Register = {
  ip1: 1,
  ip2: 2,
  port: 3,
  uptime: 9,
  push_period: 19,
  ad_hoc: 21,
  serial: 50012,
  current_primary: 57346,
  current_seconder: 57345,
}

export class Device {

  public id?: number;
  public name?: string;
  public site?: Site;
  public company?: Company;
  public type?: DeviceType;
  public status?: string;
  public modified?: string;
  public modifiedBy?: string;
  public infoList?: DeviceInfo[];
  public datapointList?: DatapointModel[];
  public parent?: Device;
  public lastcom?: string;
  public target?: string;
  public isOnline?: boolean;
  public nodeID?: number;
  public registers?: any[];

  constructor (data?: any) {
    if (data) {
      this.id = data.id;
      this.name = data.name;
      this.modified = data.modified;
      this.modifiedBy = data.modifiedBy;
      this.target = data.target;
      this.nodeID = data.nodeID;

      if (data.infoList) {
        this.infoList = data.infoList.map(i => new DeviceInfo(i));
      }

      if (data.datapointList) {
        this.datapointList = data.datapointList.map(d => new DatapointModel(d));
      }

      if (data.site) {
        this.site = new Site(data.site)
      }
      if (data.company) {
        this.company = new Company(data.company)
      }
      if (data.type) {
        this.type = new DeviceType(data.type);
      }

      if (this.site && this.company) {
        this.status = DeviceStatus.installed;
      } else if (!this.site && !this.company) {
        this.status = DeviceStatus.storage;
      } else {
        // site or company missing, something is wrong
        this.status = DeviceStatus.unknown;
      }

      if (data.parent) {
        this.parent = new Device(data.parent)
      }
      this.target = data.target;
      if (data.lastcom) {
        this.setLastCom(data.lastcom);
      }

      if (data.registers) {
        this.registers = data.registers;
      }
    }
  }

  public isValid() {
    return !!this.name && !!this.type
  }

  public getIP() {
    return this.getInfo(AttributeID.ip);
  }

  public isDatalogger() {
    if (!this.id || !this.type) {
      return false;
    }
    return +this.type.layout === LayoutID.DataLogger;
  }

  public isCTRatioVisible() {
    if (!this.id || !this.type) {
      return false;
    }
    if (!this.type.attributes || !this.type.attributes.length) {
      return false;
    }

    let hasPrimary = false;
    let hasSecondary = false;

    this.type.attributes.forEach((attr:DeviceAttribute) => {
      if (+attr.id == AttributeID.ct_primary) {
        hasPrimary = true;
      }
      if (+attr.id == AttributeID.ct_secondary) {
        hasSecondary = true;
      }
    });

    return hasPrimary && hasSecondary;
  }

  public isMeter() {
    if (!this.id || !this.type) {
      return false;
    }
    return +this.type.layout === LayoutID.ElectricityMeter || +this.type.layout == LayoutID.ImpulseMeter;
  }

  public isDevice() {
    return this.isMeter() || this.isDatalogger();
  }

  public isImpulse() {
    if (!this.id || !this.type) {
      return false;
    }
    return +this.type.layout === LayoutID.ImpulseMeter;
  }

  public getQueryInfo() {
    return {
      deviceid: this.id,
      ip: this.getInfo(AttributeID.ip),
      port: this.getInfo(AttributeID.port),
      device: this.getInfo(AttributeID.device),
      type: null
    };
  }

  public setParent(parent) {
    if (parent && parent.id) {
      this.parent = new Device(parent);
    }
  }

  public setLastCom(time) {
    if (time) {
      this.lastcom = time;

      const lastcom = moment(time);
      const now = moment();
      const duration = moment.duration(now.diff(lastcom));
      const hours = duration.asHours();
      this.setOnline(hours <= 1)
    }
  }

  public setOnline(state: boolean) {
    this.isOnline = state;
  }

  public setTarget(target) {
    if (target) {
      this.target = target;
    }
  }

  public getMeasureID(toString = false) {
    return this.getInfo(AttributeID.measureID, toString)
  }

  public getSerialNumber(toString = false) {
    return this.getInfo(AttributeID.serialNumber, toString)
  }

  public getPort() {
    let port = this.getInfo(AttributeID.port);
    return port ? port : 8000;
  }

  public getIMEI(toString = false) {
    return this.getInfo(AttributeID.imei, toString)
  }

  public getBaudRate(toString = false) {
    return this.getInfo(AttributeID.baudRate, toString)
  }

  public getParity(toString = false) {
    return this.getInfo(AttributeID.parity, toString)
  }

  public getAddress(toString = false) {
    return this.getInfo(AttributeID.device, toString)
  }

  public getDatabits(toString = false) {
    return this.getInfo(AttributeID.databits, toString)
  }

  public getIndex(toString = false) {
    return this.getInfo(AttributeID.index, toString)
  }

  public getReadFreq(toString = false) {
    return this.getInfo(AttributeID.readFreq, toString)
  }

  public getActiveLogger(toString = false) {
    return this.getInfo(AttributeID.activeLogger, toString)
  }

  public setActiveLogger(value) {
    return this.setInfo(AttributeID.activeLogger, value);
  }

  public getStartbits(toString = false) {
    return 'N/A';
    //return this.getInfo(AttributeID.startbits, toString)
  }

  public getStopbits(toString = false) {
    return this.getInfo(AttributeID.stopbits, toString)
  }

  public getPrimary(toString = false) {
    return this.getInfo(AttributeID.ct_primary, toString)
  }
  public getSecondary(toString = false) {
    return this.getInfo(AttributeID.ct_secondary, toString)
  }

  public getSync15MinAttr(toString = false) {
    return this.getInfo(AttributeID.sync15min, toString)
  }

  public setInfo(attribute, value) {
    if (this.infoList && this.infoList.length) {
      this.infoList.forEach((info:DeviceInfo) => {
        if (+info.attribute == +attribute) {
          info.value = value;
        }
      });
    }
  }

  public getCurrentTransformerRatio() {
    const primary = this.getInfo(AttributeID.ct_primary);
    const secondary = this.getInfo(AttributeID.ct_secondary);
    return `${primary ? primary : 0}/${secondary ? secondary : 0}`;
  }

  private getInfo(attr, toString = false) {
    let result = toString ? 'N/A' : null;
    if (this.infoList && this.infoList.length) {
      this.infoList.forEach((info:DeviceInfo) => {
        if (+info.attribute == +attr) {
          result = info.value;
        }
      });
    }
    return result;
  }

  public hasConfig() {
    if (!this.registers) {
      return false;
    }
    let configExists = false;
    const existingRegisters = Object.keys(this.registers);
    const index = this.getIndex();
    let startReg = (+index * 100);
    let found = 0;
    let i = 0;
    for (; i < 8; i++) {
      let reg = startReg + i;
      if (existingRegisters.includes(`${reg}`)) {
        found++
      }
    }
    return i === found;
  }
}
